;(function () {
  const STORAGE_KEY = "ghm-calculator-tracker-records";
  const COUNTER_KEY = "ghm-calculator-tracker-count";
  const STATS_KEY = "ghm-calculator-tracker-stats";
  const INTERNAL_MODE_KEY = "ghm-calculator-tracker-internal-mode";
  const MAX_RECORDS = 1000;
  const PANEL_ID = "ghm-calculator-tracker-panel";
  const PANEL_STYLE_ID = "ghm-calculator-tracker-style";

  const DEFAULT_STATS = {
    totalEvents: 0,
    calculatorViews: 0,
    variantChanges: 0,
    lastEventAt: "",
  };

  const seenSignatures = new Set();
  let internalMode = false;
  let latestTrackerData = null;

  const log = (...args) => {
    try {
      console.log("[GHM Tracker]", ...args);
    } catch (error) {}
  };

  function safeParse(raw, fallback) {
    if (!raw) return fallback;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return fallback;
    }
  }

  function getLocalValue(key, fallback) {
    try {
      return safeParse(window.localStorage.getItem(key), fallback);
    } catch (error) {
      return fallback;
    }
  }

  function setLocalValue(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  }

  function normalizeStats(rawStats) {
    const stats = { ...DEFAULT_STATS };

    if (rawStats && typeof rawStats === "object") {
      stats.totalEvents = Number(rawStats.totalEvents) || 0;
      stats.calculatorViews = Number(rawStats.calculatorViews) || 0;
      stats.variantChanges = Number(rawStats.variantChanges) || 0;
      stats.lastEventAt = String(rawStats.lastEventAt || "");
    }

    const legacyCount = Number(getLocalValue(COUNTER_KEY, 0)) || 0;
    if (legacyCount > 0) {
      stats.calculatorViews = Math.max(stats.calculatorViews, legacyCount);
      stats.totalEvents = Math.max(stats.totalEvents, legacyCount);
    }

    return stats;
  }

  function getStats() {
    return normalizeStats(getLocalValue(STATS_KEY, null));
  }

  function setStats(stats) {
    return setLocalValue(STATS_KEY, normalizeStats(stats));
  }

  function getQueryParam(name) {
    try {
      return new URL(window.location.href).searchParams.get(name);
    } catch (error) {
      return null;
    }
  }

  function resolveInternalMode(defaultValue) {
    const queryValue = getQueryParam("ghm_tracker");
    if (queryValue === "1" || queryValue === "true") {
      setLocalValue(INTERNAL_MODE_KEY, true);
      return true;
    }
    if (queryValue === "0" || queryValue === "false") {
      setLocalValue(INTERNAL_MODE_KEY, false);
      return false;
    }

    const storedValue = getLocalValue(INTERNAL_MODE_KEY, null);
    if (typeof storedValue === "boolean") return storedValue;

    return Boolean(defaultValue);
  }

  function pickValue(...values) {
    for (const value of values) {
      if (value !== undefined && value !== null && value !== "") return value;
    }
    return "";
  }

  function escapeHtml(text) {
    return String(text == null ? "" : text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function formatDateTime(timestamp) {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return timestamp;
    try {
      return date.toLocaleString("es-CL", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch (error) {
      return date.toISOString();
    }
  }

  function formatMetric(value, digits = 2) {
    const number = Number(value);
    if (!Number.isFinite(number)) return "";
    try {
      return number.toLocaleString("es-CL", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
    } catch (error) {
      return number.toFixed(digits);
    }
  }

  function getEventLabel(eventName) {
    if (eventName === "calculator_view") return "Consulta";
    if (eventName === "calculator_variant_change") return "Cambio de variante";
    if (eventName === "calculator_result") return "Calculo realizado";
    if (eventName === "calculator_acknowledged") return "Click en Entendido";
    return eventName || "Evento";
  }

  function buildRecordCalcText(record) {
    const parts = [];

    if (record.calcFormula) {
      parts.push(`Calculo: ${record.calcFormula}`);
    } else if (record.calcSummary) {
      parts.push(record.calcSummary);
    }

    if (!record.calcFormula && Number(record.totalSavings) > 0) {
      parts.push(`Ahorro ${formatMetric(record.totalSavings, 2)} kg CO2`);
    }

    if (Number(record.quantity) > 0) {
      parts.push(`Cantidad ${record.quantity}`);
    }

    return parts.filter(Boolean).join(" | ");
  }

  function csvEscape(value) {
    const text = value == null ? "" : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  }

  function getCalculationRecords(records) {
    return (records || []).filter((record) => record && record.event === "calculator_result");
  }

  function getRecordFormula(record) {
    if (record && record.calcFormula) return record.calcFormula;

    const qty = Number(record && record.quantity);
    const base = Number(record && record.basePerUnit);
    const eco = Number(record && record.ecoPerUnit);
    const totalSavings = Number(record && record.totalSavings);

    if (
      Number.isFinite(qty) && qty > 0 &&
      Number.isFinite(base) &&
      Number.isFinite(eco) &&
      Number.isFinite(totalSavings)
    ) {
      return `${qty} x (${formatMetric(base, 3)} - ${formatMetric(eco, 3)}) = ${formatMetric(totalSavings, 3)} kg CO2`;
    }

    return record && record.calcSummary ? record.calcSummary : "";
  }

  function downloadCsv() {
    const rawRecords = getLocalValue(STORAGE_KEY, []);
    const allRecords = Array.isArray(rawRecords) ? rawRecords : [];
    const calculationRecords = getCalculationRecords(allRecords);
    const records = calculationRecords;
    if (!records.length) return false;

    const header = [
      "fecha_hora",
      "evento",
      "shop_domain",
      "producto",
      "producto_id",
      "variante",
      "variante_id",
      "cantidad",
      "calculo",
      "eco_kg_co2",
      "baseline_kg_co2",
      "ahorro_kg_co2",
      "arboles_equivalentes",
      "url",
      "section_id",
    ];

    const rows = records.map((record) =>
      [
        formatDateTime(record.timestamp),
        getEventLabel(record.event),
        record.shopDomain,
        record.productTitle,
        record.productId,
        record.variantTitle,
        record.variantId,
        record.quantity,
        getRecordFormula(record),
        record.totalEco,
        record.totalBaseline,
        record.totalSavings,
        record.totalTrees,
        record.pageUrl,
        record.sectionId,
      ]
        .map(csvEscape)
        .join(","),
    );

    const csvContent = [header.join(","), ...rows].join("\n");
    const fileName = `ghm-calculator-uso-${new Date().toISOString().slice(0, 10)}.csv`;
    const payload = "\uFEFF" + csvContent;

    try {
      const blob = new Blob([payload], {
        type: "text/csv;charset=utf-8;",
      });

      if (window.URL && typeof window.URL.createObjectURL === "function") {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return true;
      }
    } catch (error) {
      log("No se pudo descargar CSV", error);
    }

    return false;
  }

  function ensurePanelStyles() {
    if (document.getElementById(PANEL_STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = PANEL_STYLE_ID;
    style.textContent = `
      #${PANEL_ID} {
        position: fixed;
        right: clamp(88px, 8vw, 132px);
        bottom: 24px;
        width: min(380px, calc(100vw - 48px));
        background: #ffffff;
        border: 1px solid #d9e1d5;
        border-radius: 12px;
        box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
        z-index: 99999;
        font-family: Arial, sans-serif;
        color: #1d2a20;
      }
      #${PANEL_ID}.is-collapsed .ghm-tracker-body {
        display: none;
      }
      #${PANEL_ID} .ghm-tracker-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 10px 12px;
        border-bottom: 1px solid #e9eee7;
        background: #f2f8ef;
        border-radius: 12px 12px 0 0;
      }
      #${PANEL_ID} .ghm-tracker-title {
        font-size: 13px;
        font-weight: 700;
      }
      #${PANEL_ID} .ghm-tracker-toggle {
        border: 1px solid #cad8c3;
        background: #fff;
        border-radius: 999px;
        padding: 4px 9px;
        font-size: 11px;
        cursor: pointer;
      }
      #${PANEL_ID} .ghm-tracker-body {
        padding: 12px;
      }
      #${PANEL_ID} .ghm-tracker-metrics {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
        margin-bottom: 10px;
      }
      #${PANEL_ID} .ghm-tracker-metric {
        background: #f7fbf6;
        border: 1px solid #e4eee0;
        border-radius: 8px;
        padding: 8px;
      }
      #${PANEL_ID} .ghm-tracker-label {
        display: block;
        font-size: 11px;
        color: #5a6a5f;
        margin-bottom: 2px;
      }
      #${PANEL_ID} .ghm-tracker-value {
        display: block;
        font-size: 18px;
        font-weight: 700;
        color: #204229;
      }
      #${PANEL_ID} .ghm-tracker-meta {
        margin: 0 0 10px;
        font-size: 11px;
        color: #516255;
      }
      #${PANEL_ID} .ghm-tracker-actions {
        display: flex;
        gap: 8px;
        margin-bottom: 10px;
      }
      #${PANEL_ID} .ghm-tracker-btn {
        flex: 1;
        border: 1px solid #c7d5c2;
        border-radius: 8px;
        background: #fff;
        color: #1d2a20;
        padding: 7px 10px;
        font-size: 12px;
        cursor: pointer;
      }
      #${PANEL_ID} .ghm-tracker-btn.btn-primary {
        background: #2f7140;
        border-color: #2f7140;
        color: #fff;
      }
      #${PANEL_ID} .ghm-tracker-records {
        border: 1px solid #e5ece2;
        border-radius: 8px;
        max-height: 180px;
        overflow: auto;
        background: #fff;
      }
      #${PANEL_ID} .ghm-tracker-row {
        padding: 8px 10px;
        border-top: 1px solid #eff3ed;
      }
      #${PANEL_ID} .ghm-tracker-row:first-child {
        border-top: 0;
      }
      #${PANEL_ID} .ghm-tracker-row-time {
        display: grid;
        gap: 4px;
        font-size: 11px;
        color: #5a6a5f;
      }
      #${PANEL_ID} .ghm-tracker-row-stamp {
        display: block;
      }
      #${PANEL_ID} .ghm-tracker-row-calc {
        display: block;
        font-size: 11px;
        line-height: 1.4;
        color: #2a5033;
      }
      #${PANEL_ID} .ghm-tracker-row-main {
        margin-top: 2px;
        font-size: 12px;
        color: #1f2b23;
      }
      #${PANEL_ID} .ghm-tracker-empty {
        padding: 10px;
        font-size: 12px;
        color: #6d7a71;
      }
      @media screen and (max-width: 749px) {
        #${PANEL_ID} {
          left: 16px;
          right: 16px;
          bottom: 92px;
          width: auto;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function ensureInternalPanel() {
    if (!internalMode) return null;
    ensurePanelStyles();

    let panel = document.getElementById(PANEL_ID);
    if (panel) return panel;

    panel = document.createElement("aside");
    panel.id = PANEL_ID;
    panel.className = "is-expanded";
    panel.innerHTML = `
      <div class="ghm-tracker-head">
        <span class="ghm-tracker-title">Tracker Calculadora (Interno)</span>
        <button type="button" class="ghm-tracker-toggle" data-ghm-action="toggle">Ocultar</button>
      </div>
      <div class="ghm-tracker-body">
        <div class="ghm-tracker-metrics">
          <div class="ghm-tracker-metric">
            <span class="ghm-tracker-label">Consultas</span>
            <span class="ghm-tracker-value" data-ghm-field="calculatorViews">0</span>
          </div>
          <div class="ghm-tracker-metric">
            <span class="ghm-tracker-label">Eventos totales</span>
            <span class="ghm-tracker-value" data-ghm-field="totalEvents">0</span>
          </div>
        </div>
        <p class="ghm-tracker-meta">Ultimo registro: <strong data-ghm-field="lastEventAt">-</strong></p>
        <div class="ghm-tracker-actions">
          <button type="button" class="ghm-tracker-btn btn-primary" data-ghm-action="download">Descargar Excel (CSV)</button>
          <button type="button" class="ghm-tracker-btn" data-ghm-action="clear">Limpiar</button>
        </div>
        <div class="ghm-tracker-records" data-ghm-field="records"></div>
      </div>
    `;

    panel.addEventListener("click", (event) => {
      const actionTarget = event.target.closest("[data-ghm-action]");
      if (!actionTarget) return;
      const action = actionTarget.dataset.ghmAction;

      if (action === "toggle") {
        panel.classList.toggle("is-collapsed");
        actionTarget.textContent = panel.classList.contains("is-collapsed")
          ? "Mostrar"
          : "Ocultar";
        return;
      }

      if (action === "download") {
        if (!downloadCsv()) {
          window.alert("No hay registros para exportar todavia.");
        }
        return;
      }

      if (action === "clear") {
        const shouldClear = window.confirm(
          "Quieres borrar todos los registros guardados en este navegador?",
        );
        if (!shouldClear) return;
        setLocalValue(STORAGE_KEY, []);
        setLocalValue(COUNTER_KEY, 0);
        setStats({ ...DEFAULT_STATS });
        updateInternalPanel();
      }
    });

    document.body.appendChild(panel);
    return panel;
  }

  function renderRecordRows(records) {
    if (!records.length) {
      return '<p class="ghm-tracker-empty">Sin registros en este navegador.</p>';
    }

    return records
      .map((record) => {
        const eventLabel = getEventLabel(record.event);
        const title = record.productTitle || "Producto sin titulo";
        const variantText = record.variantTitle ? ` | ${record.variantTitle}` : "";
        const calcText = buildRecordCalcText(record);
        return `
          <div class="ghm-tracker-row">
            <div class="ghm-tracker-row-time">
              <span class="ghm-tracker-row-stamp">${escapeHtml(formatDateTime(record.timestamp))}</span>
              ${calcText ? `<span class="ghm-tracker-row-calc">${escapeHtml(calcText)}</span>` : ""}
            </div>
            <div class="ghm-tracker-row-main">${escapeHtml(eventLabel)} | ${escapeHtml(title)}${escapeHtml(variantText)}</div>
          </div>
        `;
      })
      .join("");
  }

  function updateInternalPanel() {
    if (!internalMode) return;
    const panel = ensureInternalPanel();
    if (!panel) return;

    const stats = getStats();
    const records = getLocalValue(STORAGE_KEY, []).slice(-12).reverse();

    const viewsEl = panel.querySelector('[data-ghm-field="calculatorViews"]');
    const totalEl = panel.querySelector('[data-ghm-field="totalEvents"]');
    const lastEl = panel.querySelector('[data-ghm-field="lastEventAt"]');
    const recordsEl = panel.querySelector('[data-ghm-field="records"]');

    if (viewsEl) viewsEl.textContent = String(stats.calculatorViews);
    if (totalEl) totalEl.textContent = String(stats.totalEvents);
    if (lastEl) lastEl.textContent = formatDateTime(stats.lastEventAt);
    if (recordsEl) recordsEl.innerHTML = renderRecordRows(records);
  }

  function exposeApi() {
    window.GHMCalculatorTracker = {
      clear() {
        setLocalValue(STORAGE_KEY, []);
        setLocalValue(COUNTER_KEY, 0);
        setStats({ ...DEFAULT_STATS });
        updateInternalPanel();
      },
      downloadCsv,
      getCount() {
        return getStats().calculatorViews;
      },
      getRecords() {
        return getLocalValue(STORAGE_KEY, []);
      },
      getStats,
      refreshInternalPanel: updateInternalPanel,
      trackEvent(eventName, payload, options) {
        const normalizedEventName = String(eventName || "").trim();
        if (!normalizedEventName) return;

        const extraRecord = payload && typeof payload === "object" ? payload : {};
        const baseData = latestTrackerData && typeof latestTrackerData === "object"
          ? latestTrackerData
          : {};

        track({ ...baseData, ...extraRecord }, normalizedEventName, extraRecord, options);
      },
      enablePanel() {
        internalMode = true;
        setLocalValue(INTERNAL_MODE_KEY, true);
        updateInternalPanel();
      },
      disablePanel() {
        internalMode = false;
        setLocalValue(INTERNAL_MODE_KEY, false);
        const panel = document.getElementById(PANEL_ID);
        if (panel) panel.remove();
      },
    };
  }

  function getTrackerScript() {
    return document.currentScript || document.querySelector("script[data-ghm-calculator-tracker]");
  }

  function getTrackerData(sectionId) {
    const dataEl = document.getElementById(`ghm-calculator-tracker-data-${sectionId}`);
    return safeParse(dataEl && dataEl.textContent, null);
  }

  function getTrackerDataFromHtml(htmlDoc, sectionId) {
    if (!htmlDoc || typeof htmlDoc.getElementById !== "function") return null;
    return safeParse(
      htmlDoc.getElementById(`ghm-calculator-tracker-data-${sectionId}`)?.textContent,
      null,
    );
  }

  function buildSignature(record) {
    return [record.event, record.sectionId, record.productId, record.variantId].join("|");
  }

  function persistRecord(record) {
    const records = getLocalValue(STORAGE_KEY, []);
    records.push(record);
    setLocalValue(STORAGE_KEY, records.slice(-MAX_RECORDS));

    const stats = getStats();
    stats.totalEvents += 1;
    if (record.event === "calculator_view") {
      stats.calculatorViews += 1;
    }
    if (record.event === "calculator_variant_change") {
      stats.variantChanges += 1;
    }
    stats.lastEventAt = record.timestamp;

    setStats(stats);
    setLocalValue(COUNTER_KEY, stats.calculatorViews);
    updateInternalPanel();
  }

  async function sendRecord(record, endpoint) {
    if (!endpoint) return;
    try {
      await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(record),
        keepalive: true,
      });
    } catch (error) {
      log("No se pudo enviar el registro remoto", error);
    }
  }

  function track(data, eventName, extraRecord = {}, options = {}) {
    const normalizedEventName = String(eventName || "").trim();
    if (!normalizedEventName) return;
    if (!data || !data.productId) return;

    const record = {
      timestamp: pickValue(extraRecord.timestamp, new Date().toISOString()),
      event: normalizedEventName,
      shopDomain: pickValue(extraRecord.shopDomain, data.shopDomain, window.location.hostname),
      sectionId: pickValue(extraRecord.sectionId, data.sectionId),
      productId: pickValue(extraRecord.productId, data.productId),
      productHandle: pickValue(extraRecord.productHandle, data.productHandle),
      productTitle: pickValue(extraRecord.productTitle, data.productTitle, document.title),
      variantId: pickValue(extraRecord.variantId, data.variantId),
      variantTitle: pickValue(extraRecord.variantTitle, data.variantTitle),
      pageUrl: pickValue(extraRecord.pageUrl, window.location.href),
      userAgent: pickValue(extraRecord.userAgent, navigator.userAgent),
      quantity: pickValue(extraRecord.quantity),
      calcFormula: pickValue(extraRecord.calcFormula),
      calcSummary: pickValue(extraRecord.calcSummary),
      ecoPerUnit: pickValue(extraRecord.ecoPerUnit),
      basePerUnit: pickValue(extraRecord.basePerUnit),
      savingsPerUnit: pickValue(extraRecord.savingsPerUnit),
      totalEco: pickValue(extraRecord.totalEco),
      totalBaseline: pickValue(extraRecord.totalBaseline),
      totalSavings: pickValue(extraRecord.totalSavings),
      totalTrees: pickValue(extraRecord.totalTrees),
      unitLabel: pickValue(extraRecord.unitLabel),
      ackSource: pickValue(extraRecord.ackSource),
    };

    if (options.dedupe !== false) {
      const signature = buildSignature(record);
      if (seenSignatures.has(signature)) return;
      seenSignatures.add(signature);
    }

    persistRecord(record);
    sendRecord(record, data.trackingEndpoint);
    log("Consulta registrada", record);
  }

  function init() {
    const trackerScript = getTrackerScript();
    const sectionId = trackerScript?.dataset.sectionId;
    if (!sectionId) return;

    let trackerData = getTrackerData(sectionId);
    if (!trackerData) return;
    latestTrackerData = { ...trackerData };

    internalMode = resolveInternalMode(trackerData.internalMode);
    exposeApi();
    updateInternalPanel();
    track(trackerData, "calculator_view");

    window.addEventListener("ghm:calculator-track", (event) => {
      const detail = event?.detail || {};
      const eventName = String(detail.eventName || detail.event || "").trim();
      if (!eventName) return;

      const extraRecord = { ...detail };
      delete extraRecord.eventName;
      delete extraRecord.event;

      track({ ...trackerData, ...extraRecord }, eventName, extraRecord, {
        dedupe: false,
      });
    });

    const handleVariantChange = (event) => {
      const updatedData =
        getTrackerDataFromHtml(event?.data?.html, sectionId) || trackerData;
      trackerData = { ...trackerData, ...updatedData };
      latestTrackerData = { ...trackerData };
      internalMode = resolveInternalMode(trackerData.internalMode);
      track(trackerData, "calculator_variant_change");
    };

    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event?.data?.sectionId !== sectionId) return;
        handleVariantChange(event);
      });
    }
  }

  init();
})();
